/**
 * GET /client/{mcode}/subclients/{ccode}/members/{subId}/invoices — portal member invoice history.
 * Normalizer accepts camelCase (ASP.NET default) or PascalCase payloads.
 */

export interface MemberInvoiceLinePortalDto {
  lineId: number;
  description?: string | null;
  product?: string | null;
  coverageStartDate?: string | null;
  invoicedAmount?: number | null;
  amountPaid?: number | null;
  creditAmount?: number | null;
  balanceDue?: number | null;
}

export interface MemberInvoicePortalDto {
  invoiceId: number;
  invoiceDate?: string | null;
  clientName?: string | null;
  totalInvoiced: number;
  totalPaid: number;
  totalCredit: number;
  totalBalance: number;
  lines: MemberInvoiceLinePortalDto[];
}

function strOrNull(v: unknown): string | null | undefined {
  if (v === null) return null;
  if (v === undefined) return undefined;
  const s = String(v).trim();
  return s === '' ? null : s;
}

function num(v: unknown): number {
  if (v == null || v === '') return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function numOrNull(v: unknown): number | null | undefined {
  if (v === null) return null;
  if (v === undefined) return undefined;
  if (v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function lineIdFromRaw(raw: Record<string, unknown>): number {
  const id = num(raw['lineId'] ?? raw['LineId']);
  return id;
}

function lineFromRaw(raw: Record<string, unknown>): MemberInvoiceLinePortalDto {
  return {
    lineId: lineIdFromRaw(raw),
    description: strOrNull(raw['description'] ?? raw['Description']),
    product: strOrNull(raw['product'] ?? raw['Product']),
    coverageStartDate: strOrNull(raw['coverageStartDate'] ?? raw['CoverageStartDate']),
    invoicedAmount: numOrNull(raw['invoicedAmount'] ?? raw['InvoicedAmount']),
    amountPaid: numOrNull(raw['amountPaid'] ?? raw['AmountPaid']),
    creditAmount: numOrNull(raw['creditAmount'] ?? raw['CreditAmount']),
    balanceDue: numOrNull(raw['balanceDue'] ?? raw['BalanceDue'])
  };
}

function invoiceFromRaw(raw: Record<string, unknown>): MemberInvoicePortalDto {
  const linesRaw = raw['lines'] ?? raw['Lines'];
  const lines: MemberInvoiceLinePortalDto[] = Array.isArray(linesRaw)
    ? (linesRaw as unknown[]).map((row) =>
        row && typeof row === 'object' ? lineFromRaw(row as Record<string, unknown>) : null
      ).filter((x): x is MemberInvoiceLinePortalDto => x != null && Number.isFinite(x.lineId))
    : [];

  return {
    invoiceId: num(raw['invoiceId'] ?? raw['InvoiceId']),
    invoiceDate: strOrNull(raw['invoiceDate'] ?? raw['InvoiceDate']),
    clientName: strOrNull(raw['clientName'] ?? raw['ClientName']),
    totalInvoiced: num(raw['totalInvoiced'] ?? raw['TotalInvoiced']),
    totalPaid: num(raw['totalPaid'] ?? raw['TotalPaid']),
    totalCredit: num(raw['totalCredit'] ?? raw['TotalCredit']),
    totalBalance: num(raw['totalBalance'] ?? raw['TotalBalance']),
    lines
  };
}

export function normalizeMemberInvoicesResponse(body: unknown): MemberInvoicePortalDto[] {
  if (!Array.isArray(body)) {
    return [];
  }
  return body
    .map((row) => (row && typeof row === 'object' ? invoiceFromRaw(row as Record<string, unknown>) : null))
    .filter((x): x is MemberInvoicePortalDto => x != null && Number.isFinite(x.invoiceId));
}
